import { TranslateService } from '@ngx-translate/core';
import { Component, OnInit, ViewChild, NgZone } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { NgbModal, NgbModalRef, ModalDismissReasons } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { CompanyService } from 'src/app/services/company.service';
import { setCompanies } from 'src/app/actions/companies.actions';
import { setOffice, setProjects } from 'src/app/actions/consultant.actions';
import { fromCompanies } from 'src/app/reducers/companies.reducer';
import { Company, Office } from 'src/app/models/office.model';
import { setJobtypes } from 'src/app/actions/jobtypes.actions';
import { setTestModels } from 'src/app/actions/test-models.actions';
import { fromConsultant } from 'src/app/reducers/consultant.reducer';
import { setProcesses } from 'src/app/actions/processes.actions';
import { ProcessService } from 'src/app/services/process.service';
import { TestService } from 'src/app/services/test.service';
import { JobtypeService } from 'src/app/services/jobtype.service';
import { UtilsService } from 'src/app/services/utils.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { Consultant } from 'src/app/models/consultant.model';
import { AppConfig } from '../../../helpers/app.config';
import * as helpers from '../../../helpers/helpers';

@Component({
  selector: 'ct-office-selector',
  templateUrl: './office-selector.component.html',
  styleUrls: ['./office-selector.component.scss']
})
export class OfficeSelectorComponent implements OnInit {

  @ViewChild('content') content;

  modalRef: NgbModalRef;
  closeResult: string;
  companies$: Observable<Company[]>;
  collapsedCompanies: { id: string, collapsed: boolean }[] = [];
  consultant$: Observable<Consultant>;

  HCCOffice = AppConfig.settings.OFFICES_WITH_PROJECTS.split(',').map(officeId => Number(officeId));

  constructor(
    private store: Store,
    private ngZone: NgZone,
    private modal: NgbModal,
    private companyService: CompanyService,
    private jobtypeService: JobtypeService,
    private testService: TestService,
    private processService: ProcessService,
    private utilsService: UtilsService,
    private authenticationService: AuthenticationService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.consultant$ = this.store.pipe(select(fromConsultant.selectConsultantState));
  }

  open() {
    if (!this.modal.hasOpenModals()) {
      this.companies$ = this.store.pipe(select(fromCompanies.selectCompaniesState), map(companies => {
        for (const company of companies) {
          this.collapsedCompanies.push({ id: company.id, collapsed: false });
        }
        return companies;
      }));

      // Agregado ngZone.run porque el servicio se ejecutaba fuera de la ngZone por lo que el evento del dispatch no era detectado
      this.ngZone.run(() => {
        // ¿Sustituir por effects?
        this.companyService.index().subscribe(
          companies => {
            if (companies.length) {
              this.store.dispatch(setCompanies({ companies }));
            } else {
              this.utilsService.toast('warning', 'NoPermissions');
              this.authenticationService.logOut('consultant');
              setTimeout(() => {
                location.href = AppConfig.settings.LOGOUT_FBI;
              }, 5000);
            }
          },
          error => {
            this.utilsService.toast('error', 'CannotGetOffices', true, this.utilsService.getNacsData({ endpoint: 'GET' + AppConfig.settings.API + ' /offices', body: '', error: { errorType: error.errorType, errorCode: error.errorCode, message: error.message } }));
            if (this.modal.hasOpenModals()) {
              this.modal.dismissAll('Cross click');
            }
            this.authenticationService.logOut('consultant');
          }
        );
      });

      this.consultant$.pipe(take(1)).subscribe(consultant => {
        let backdrop;
        let keyboard;
        if (consultant.office) {
          backdrop = true;
          keyboard = true;
        } else {
          backdrop = 'static';
          keyboard = false;
        }
        this.modalRef = this.modal.open(this.content, { ariaLabelledBy: 'Selector-Oficinas', backdrop, keyboard });
        this.modalRef.result.then((result) => {
          this.closeResult = 'Closed with: ' + result;
        }, (reason) => {
          this.closeResult = 'Dismissed ' + this.getDismissReason(reason);
        });
      });
    }
  }

  getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return 'with: ' + reason;
    }
  }

  selectOffice(office: Office) {
    const businessAreas = helpers.getBusinessFromOffice(office);
    const businessAreaIds = helpers.getBusinessIdsFromBussiness(businessAreas);
    const companyTypeId = businessAreaIds[businessAreaIds.length - 1];

    this.store.dispatch(setOffice({
      ...office,
      businessAreas,
      businessAreaIds
    }));
    this.modalRef.close('Office Selected');

    this.ngZone.run(() => {
      this.jobtypeService.index(companyTypeId, 1).subscribe(
        jobtypes => {
          jobtypes.map(jobtype => jobtype.name = this.translateService.instant('JOBTYPES.' + jobtype.randstadJobtype))
          this.store.dispatch(setJobtypes({ jobtypes }))
        },
        error => {
          this.utilsService.toast('error', 'CannotGetJobtypes', true, this.utilsService.getNacsData({ endpoint: 'GET' + AppConfig.settings.API + ' /jobtypes', body: { companyTypeId: companyTypeId }, error: { errorType: error.errorType, errorCode: error.errorCode, message: error.message } }));
          this.authenticationService.logOut('consultant');
          return false;
        }
      );

      this.testService.getModels().subscribe(
        testModels => this.store.dispatch(setTestModels({ testModels })),
        error => {
          this.utilsService.toast('error', 'CannotGetTestModels', true, this.utilsService.getNacsData({ endpoint: 'GET' + AppConfig.settings.API + ' /tests/types', body: '', error: { errorType: error.errorType, errorCode: error.errorCode, message: error.message } }));
          this.authenticationService.logOut('consultant');
          return false;
        }
      );

      this.processService.search({ name: '', clientId: 1, officeId: office.id }, 0).pipe(take(1)).subscribe(
        processes => { this.store.dispatch(setProcesses(processes)); },
        error => {
          this.utilsService.toast('error', 'CannotGetProcesses', true, this.utilsService.getNacsData({ endpoint: 'GET' + AppConfig.settings.API + ' /processes?origin=ct', body: { name: '', clientId: 1, officeId: office.id }, error: { errorType: error.errorType, errorCode: error.errorCode, message: error.message } }))
          this.authenticationService.logOut('consultant');
          return false;
        }
      );

      if (this.HCCOffice.includes(office.randstadOffice)) {
        this.utilsService.getClientProjects(office.randstadOffice).subscribe(clientProjects => {
          this.store.dispatch(setProjects({ projects: clientProjects.projects }));
        });
      }
    });
  }

  filterOffices(search: string) {
    this.ngZone.run(() => {
      this.companies$ = this.store.pipe(select(fromCompanies.selectFilteredCompanies(search)));
    });
  }

  // Un array que contiene las compañias con un boolean collapsed para mantener esta logica separada del array original
  // la funcion retorna si la compañia esta colapsada y si se envia true como segundo parametro se alterna su valor
  isCompanyCollapsed(company, collapse = false): boolean {
    const collapsedCompany = this.collapsedCompanies.find(item => item.id === company.id);
    if (collapse) {
      collapsedCompany.collapsed = !collapsedCompany.collapsed;
    }
    return collapsedCompany.collapsed;
  }

}
